import { DeleteIcon, EditIcon, ExternalLinkIcon, ViewIcon } from '@chakra-ui/icons';
import {
    AlertDialog,
    AlertDialogBody,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogOverlay,
    Badge,
    Box, Button, Center, Divider, HStack, Input,
    SimpleGrid,
    Table, Tbody, Td, Text, Th, Thead, Tr,
    useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import {
    deletePhysician,
    fetchPhysicians,
    getAllFacilitiesOutside,
    sendPhysicianSignatureLink
} from '../../../api';
import AccessDenied from '../../../components/AccessDenied';
import Loading from '../../../components/Loading/Loading';
import Pagination from '../../../components/Pagination';
import PopoverId from '../../../components/PopoverId';
import TitleHeader from '../../../components/TitleHeader';
import { hasPermission } from '../../../Utils';
const PhysiciansPage = () => {
    const [physicians, setPhysicians] = useState([]);
    const [searchParams, setSearchParams] = useState({
        fullName: '',
        specialty: '',
        npiNumber: '',
        phone: '',
        email: ''
    });
    const [facilities, setFacilities] = useState([]);
    const [facilitySelect, setFacilitySelect] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(true);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [signatureTarget, setSignatureTarget] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [sendingSignature, setSendingSignature] = useState(false);

    const [tempEmail, setTempEmail] = useState('');
    const [tempPhone, setTempPhone] = useState('');

    const toast = useToast();
    const cancelRef = useRef();
    const navigate = useNavigate();
    const itemsPerPage = 25;

    const fetchData = async (page = 1, params = {}) => {
        setLoading(true);
        const query = new URLSearchParams({ page, limit: itemsPerPage, ...params }).toString();
        const result = await fetchPhysicians(query);
        if (result.success) {
            setPhysicians(result.data.physicians);
            setCurrentPage(result.data.currentPage);
            setTotalPages(result.data.totalPages);
        } else {
            setError(result.error || 'Failed to load physicians');
        }


        try {
            const response = await getAllFacilitiesOutside();
            if (response.success) {
                setFacilities(response.data.facilities);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to load facilities.',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleDelete = async (id) => {
        const res = await deletePhysician(id);
        if (res.success) {
            toast({
                title: 'Deleted',
                description: 'Physician deleted',
                status: 'success',
                duration: 3000,
                isClosable: true
            });
            setPhysicians(prev => prev.filter(p => p._id !== id));
        } else {
            toast({
                title: 'Error',
                description: res.message,
                status: 'error',
                duration: 3000,
                isClosable: true
            });
        }
        setDeleteTarget(null); // close modal
    };

    const handleConfirmSignatureSend = async () => {
        if (!signatureTarget || !selectedMethod) return;
        setSendingSignature(true);

        const contactValue = selectedMethod === 'email' ? tempEmail : tempPhone;

        const res = await sendPhysicianSignatureLink(signatureTarget._id, selectedMethod, contactValue);

        console.log(res)

        toast({
            title: res.success ? 'Success' : 'Error',
            description: res.message,
            status: res.success ? 'success' : 'error',
            duration: 3000,
            isClosable: true
        });

        setSignatureTarget(null);
        setSelectedMethod(null);
        setSendingSignature(false);
    };


    const handleSearch = async () => {
        setSearching(true);
        await fetchData(1, searchParams);
    };

    const handleClearSearch = () => {
        setSearchParams({
            fullName: '',
            specialty: '',
            npiNumber: '',
            phone: '',
            email: ''
        });
        setSearching(false);
        fetchData(1);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prev => ({ ...prev, [name]: value }));
    };

    const handlePageChange = (page) => {
        fetchData(page, searchParams);
    };


    useEffect(() => {
        if (signatureTarget) {
            setTempEmail(signatureTarget?.contact?.email || '');
            const lastSig = signatureTarget?.signature?.length
                ? [...signatureTarget.signature].sort((a, b) =>
                    new Date(b.metadata?.signedAt || 0) - new Date(a.metadata?.signedAt || 0)
                )[0]
                : null;
            setTempPhone(lastSig?.phone || '');
        }
    }, [signatureTarget]);


    if (error) return <AccessDenied msg={error} />;

    return (
        <Box maxW="full" mx="auto" p={8}>
            <VStack spacing={6} align="stretch">
                <TitleHeader title="Physicians" buttonText="New Physician" onClick={() => navigate('/reqs/physicians/create')} />

                {/* Search */}
                <Text fontSize="lg">Search Physicians </Text>

                <VStack spacing={4} mb={5} width="full">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} width="full">
                        <Input
                            name="fullName"
                            placeholder="Full Name"
                            value={searchParams.fullName}
                            onChange={handleInputChange}
                        />
                        <Input
                            name="npiNumber"
                            placeholder="NPI Number"
                            value={searchParams.npiNumber}
                            onChange={handleInputChange}
                        />
                        <Input
                            name="phone"
                            placeholder="Phone"
                            value={searchParams.phone}
                            onChange={handleInputChange}
                        />
                        <Select
                            options={facilities.map(fac => ({
                                value: fac._id,
                                label: `${fac.name} - ${fac.address?.city || 'N/A'}`
                            }))}
                            onChange={(option) => setFacilitySelect(option)}
                            value={facilitySelect}
                            placeholder="Select a facility"
                            isClearable
                        />
                    </SimpleGrid>

                    <HStack spacing={4}>
                        <Button onClick={handleSearch} colorScheme="teal" size="sm" isLoading={loading}>
                            Search
                        </Button>
                        {searching && (
                            <Button onClick={handleClearSearch} colorScheme="red" size="sm">
                                Clear
                            </Button>
                        )}
                    </HStack>
                </VStack>

                <Divider />

                {loading ? (
                    <Center mt="20%"><Loading /></Center>
                ) : (
                    <Table size="sm">
                        <Thead>
                            <Tr>
                                <Th>#</Th>
                                <Th>Full Name</Th>
                                <Th>NPI</Th>
                                <Th>License #</Th>
                                <Th>Affiliated Client</Th>
                                <Th>Verified</Th>
                                <Th>Actions</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {physicians.map((physician, index) => (
                                <Tr key={physician._id} _hover={{ bg: 'gray.100' }}>
                                    <Td><PopoverId place='Physicians' index={index + (currentPage - 1) * itemsPerPage} content={physician._id} /></Td>
                                    <Td>{physician.fullName}</Td>
                                    <Td>{physician.npiNumber}</Td>
                                    <Td>{physician.license?.number || 'N/A'}</Td>
                                    <Td>
                                        <Text
                                            cursor="pointer"
                                            onClick={() => window.open(`/facilities/${physician?.affiliatedFacility?._id}`, '_blank')}
                                            display="inline-flex"
                                            alignItems="center"
                                            color="blue.600"
                                        >
                                            {physician.affiliatedFacility?.name || 'N/A'} <ExternalLinkIcon ml={1} />
                                        </Text>
                                    </Td>
                                    <Td>
                                        {Array.isArray(physician.signature) && physician.signature.length > 0
                                            ? [...physician.signature]
                                                .sort((a, b) => new Date(b.metadata?.signedAt) - new Date(a.metadata?.signedAt))[0]
                                                ?.verified ? (
                                                <Badge colorScheme="green">Yes</Badge>
                                            ) : (
                                                <Badge colorScheme="red">No</Badge>
                                            )
                                            : <Badge colorScheme="gray">N/A</Badge>}
                                    </Td>

                                    <Td>
                                        <HStack spacing={2}>
                                            <Button size="xs" colorScheme="blue" onClick={() => navigate(`/reqs/physicians/view/${physician._id}`)}>
                                                <ViewIcon mr={1} /> View
                                            </Button>
                                            {hasPermission('editPhysicians') &&
                                                <Button size="xs" colorScheme="green" onClick={() => navigate(`/reqs/physicians/edit/${physician._id}`)}>
                                                    <EditIcon mr={1} /> Edit
                                                </Button>
                                            }
                                            {hasPermission('sendPhysicianLink') && (
                                                <Button
                                                    size="xs"
                                                    colorScheme="orange"
                                                    onClick={() => setSignatureTarget(physician)}
                                                >
                                                    Request Signature
                                                </Button>
                                            )}

                                            {hasPermission('deletePhysicians') &&
                                                <Button
                                                    size="xs"
                                                    colorScheme="red"
                                                    onClick={() => setDeleteTarget(physician)}
                                                >
                                                    <DeleteIcon mr={1} /> Delete
                                                </Button>
                                            }
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                )}

                <Pagination currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
            </VStack>



            <AlertDialog
                isOpen={!!deleteTarget}
                leastDestructiveRef={cancelRef}
                onClose={() => setDeleteTarget(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Delete Physician
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            Are you sure you want to delete{' '}
                            <strong>{deleteTarget?.fullName}</strong>? This action cannot be undone.
                        </AlertDialogBody>

                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setDeleteTarget(null)}>
                                Cancel
                            </Button>
                            <Button colorScheme="red" onClick={() => handleDelete(deleteTarget._id)} ml={3}>
                                Delete
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <AlertDialog
                isOpen={!!signatureTarget}
                leastDestructiveRef={cancelRef}
                onClose={() => setSignatureTarget(null)}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="lg" fontWeight="bold">
                            Send Signature Link
                        </AlertDialogHeader>

                        <AlertDialogBody>
                            <Text mb={2}>
                                Send a signature request to <strong>{signatureTarget?.fullName}</strong> via:
                            </Text>

                            <Center>
                                <HStack mb={6} mt={6}>
                                    <Button
                                        size="sm"
                                        variant={selectedMethod === 'email' ? 'solid' : 'outline'}
                                        colorScheme="blue"
                                        onClick={() => setSelectedMethod('email')}
                                    >
                                        Email
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant={selectedMethod === 'sms' ? 'solid' : 'outline'}
                                        colorScheme="green"
                                        onClick={() => setSelectedMethod('sms')}
                                    >
                                        Phone
                                    </Button>
                                </HStack>

                            </Center>

                            {selectedMethod === 'email' && (
                                <Box>
                                    <Text fontSize="sm" mb={1}>Email:</Text>
                                    <Input value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} />
                                </Box>
                            )}

                            {selectedMethod === 'sms' && (
                                <Box>
                                    <Text fontSize="sm" mb={1}>Phone:</Text>
                                    <Input
                                        value={tempPhone} onChange={(e) => setTempPhone(e.target.value)}
                                    />
                                </Box>
                            )}
                        </AlertDialogBody>


                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setSignatureTarget(null)}>
                                Cancel
                            </Button>
                            <Button
                                colorScheme="orange"
                                ml={3}
                                isDisabled={!selectedMethod}
                                isLoading={sendingSignature}
                                onClick={handleConfirmSignatureSend}
                            >
                                Send Link
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>


        </Box>
    );
};

export default PhysiciansPage;
